import React from 'react';
import { ResponsiveLine } from '@nivo/line';
import { formatDate, isWithinRange } from 'shared/src/date-string-utils/date-utils';
import { ISODateString } from 'shared/src/date-string-utils/types';
import { formatMoney } from 'shared/src/utils/money/money-utils';
import { Day, getUncoveredDateRangesV2 } from './pacing-helpers';
import { PacingBlockV2 } from 'shared/src/api/line-item-types';

type PacingChartMode = 'light' | 'dark';

export function PacingChart({
  data,
  blocks,
  mode = 'light'
}: {
  data: Day[];
  blocks: PacingBlockV2[];
  mode?: PacingChartMode;
}) {
  const maxY = Math.max(...data.map(d => d.cumulativeTotal));
  const isDark = mode === 'dark';

  // Colors based on mode
  const axisTextColor = isDark ? '#e5e7eb' : '#4b5563'; // gray-200 / gray-600
  const tooltipBg = isDark ? 'bg-gray-900 border border-gray-700' : 'bg-white';
  const tooltipLabelText = isDark ? 'text-gray-400' : 'text-gray-400';
  const tooltipValueText = isDark ? 'text-gray-100' : 'text-gray-900';

  const nivoTheme = {
    axis: {
      ticks: {
        text: {
          fill: axisTextColor,
          fontSize: 11
        }
      },
      legend: {
        text: {
          fill: axisTextColor,
          fontSize: 12
        }
      }
    },
    tooltip: {
      container: {
        background: isDark ? '#111827' : '#ffffff',
        color: isDark ? '#e5e7eb' : '#111827'
      }
    }
  };

  // Block colors matching the block components
  const BLOCK_COLORS = ['#a855f7', '#14b8a6', '#3b82f6', '#ec4899']; // purple, teal, blue, pink
  const UNCOVERED_COLOR = '#e5e7eb'; // gray-200

  const startDate = data[0]?.day;
  const endDate = data[data.length - 1]?.day;

  let chartData: { id: string; data: { x: string; y: number }[]; color: string }[] = [];

  if (blocks.length > 0 && startDate && endDate) {
    const uncoveredRanges = getUncoveredDateRangesV2(blocks, startDate, endDate);

    const coveredSeries = blocks.map((block, idx) => {
      const blockData = data
        .filter(day => isWithinRange(day.day, block.dates))
        .map(day => ({ x: day.day, y: day.cumulativeTotal }));

      return {
        id: `Block ${idx + 1}`,
        data: blockData,
        color: BLOCK_COLORS[idx % BLOCK_COLORS.length]
      };
    });

    const uncoveredSeries = uncoveredRanges.map((range, idx) => {
      const rangeData = data
        .filter(day => isWithinRange(day.day, range))
        .map(day => ({ x: day.day, y: day.cumulativeTotal }));

      return {
        id: `Uncovered ${idx + 1}`,
        data: rangeData,
        color: UNCOVERED_COLOR
      };
    });

    chartData = [...coveredSeries, ...uncoveredSeries];
  } else {
    const fullLineData = data.map(day => ({ x: day.day, y: day.cumulativeTotal }));
    chartData = [{ id: 'Full Range', data: fullLineData, color: UNCOVERED_COLOR }];
  }

  return (
    <div className="h-[250px] 0">
      <ResponsiveLine
        theme={nivoTheme}
        margin={{ top: 20, right: 20, bottom: 60, left: 90 }}
        animate
        data={chartData}
        colors={d => (d.color as string) || BLOCK_COLORS[0]}
        enableSlices="x"
        sliceTooltip={({ slice }) => (
          <div className={`mt-10 rounded-sm p-4 shadow-xl ${tooltipBg}`}>
            <div className="flex justify-between">
              <div className={`text-sm font-light ${tooltipLabelText}`}>Date:</div>
              <div className={tooltipValueText}>
                {formatDate(ISODateString.parse(slice.points[0].data.xFormatted))}
              </div>
            </div>
            <div className="flex justify-between">
              <div className={`mr-6 text-sm font-light ${tooltipLabelText}`}>Cumulative Total:</div>
              <div className={tooltipValueText}>
                {formatMoney(slice.points[0].data.y as number)}
              </div>
            </div>
          </div>
        )}
        xScale={{
          type: 'time',
          format: '%Y-%m-%d',
          useUTC: false,
          precision: 'day'
        }}
        xFormat="time:%Y-%m-%d"
        yScale={{
          type: 'linear',
          max: maxY * 1.25
        }}
        enableArea
        areaOpacity={0.7}
        axisLeft={{
          legend: 'Cumulative Daily Spend',
          legendOffset: -60,
          legendPosition: 'middle',
          format: (value: number) => `$${value}`
        }}
        axisBottom={{
          format: '%b %d',
          tickValues: calcTickValues(data.length)
        }}
        enablePoints={false}
        useMesh={true}
        defs={[
          {
            id: 'purpleGradient',
            type: 'linearGradient',
            colors: [
              { offset: 0, color: 'rgba(168, 85, 247, 0.8)' },
              { offset: 100, color: 'rgba(168, 85, 247, 0.3)' }
            ]
          },
          {
            id: 'tealGradient',
            type: 'linearGradient',
            colors: [
              { offset: 0, color: 'rgba(20, 184, 166, 0.8)' },
              { offset: 100, color: 'rgba(20, 184, 166, 0.3)' }
            ]
          },
          {
            id: 'blueGradient',
            type: 'linearGradient',
            colors: [
              { offset: 0, color: 'rgba(59, 130, 246, 0.8)' },
              { offset: 100, color: 'rgba(59, 130, 246, 0.3)' }
            ]
          },
          {
            id: 'pinkGradient',
            type: 'linearGradient',
            colors: [
              { offset: 0, color: 'rgba(236, 72, 153, 0.8)' },
              { offset: 100, color: 'rgba(236, 72, 153, 0.3)' }
            ]
          },
          {
            id: 'grayGradient',
            type: 'linearGradient',
            colors: [
              { offset: 0, color: 'rgba(229, 231, 235, 0.8)' },
              { offset: 100, color: 'rgba(229, 231, 235, 0.3)' }
            ]
          }
        ]}
        fill={[
          { match: { id: 'Block 1' }, id: 'purpleGradient' },
          { match: { id: 'Block 2' }, id: 'tealGradient' },
          { match: { id: 'Block 3' }, id: 'blueGradient' },
          { match: { id: 'Block 4' }, id: 'pinkGradient' },
          { match: d => String(d.id).startsWith('Uncovered'), id: 'grayGradient' },
          { match: { id: 'Full Range' }, id: 'grayGradient' }
        ]}
      />
    </div>
  );
}

function calcTickValues(numDays: number) {
  if (numDays < 360) return 'every 1 months';
  else if (numDays < 720) return 'every 2 months';
  else if (numDays < 1800) return 'every 3 months';
  else return 'every 6 months';
}
